// Сетевой клиент онлайн-режима: REST для комнат + WebSocket для состояния.
import type { Action, PlayerId, Room, ServerMessage, ViewMessage, BotLevel } from '../types.ts';
import type { NetStatus } from './state.ts';
import { clientId, deviceKey } from './outbox.ts';

const LS_KEY = 'krutagidon-online';

/** Сессия комнаты, запомненная в localStorage (чтобы вернуться после обрыва) */
export interface SavedSession {
  code: string;
  token: string;
  pid: PlayerId;
  name: string;
}

export interface NetHandlers {
  onView(m: ViewMessage): void;
  onError(msg: string): void;
  onStatus(s: NetStatus): void;
}

interface RoomResponse {
  code?: string;
  token?: string;
  pid?: number;
  error?: string;
}

function isSavedSession(x: unknown): x is SavedSession {
  if (!x || typeof x !== 'object') return false;
  const s = x as Partial<SavedSession>;
  return typeof s.code === 'string' && typeof s.token === 'string' && typeof s.pid === 'number' && typeof s.name === 'string';
}

function isServerMessage(x: unknown): x is ServerMessage {
  if (!x || typeof x !== 'object') return false;
  const t = (x as { type?: unknown }).type;
  return t === 'view' || t === 'error';
}

async function postJson(url: string, body: unknown): Promise<RoomResponse> {
  const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  let j: RoomResponse = {};
  try {
    j = (await r.json()) as RoomResponse;
  } catch {
    /* не JSON — ниже сообщим об ошибке */
  }
  if (!r.ok) throw new Error(j.error ?? `ошибка сервера (${r.status})`);
  return j;
}

export class NetClient {
  private readonly handlers: NetHandlers;
  code: string | null = null;
  token: string | null = null;
  pid: PlayerId | null = null;
  name: string | null = null;
  room: Room | null = null;
  private ws: WebSocket | null = null;
  private closed = false;
  private retry = 0;
  connected = false;
  /** партия по переписке: вход — из списка партий (токен у сервера), сессия в localStorage не запоминается */
  corr = false;

  constructor(handlers: NetHandlers) {
    this.handlers = handlers;
  }

  static saved(): SavedSession | null {
    try {
      const s: unknown = JSON.parse(localStorage.getItem(LS_KEY) ?? 'null');
      return isSavedSession(s) ? s : null;
    } catch {
      return null;
    }
  }
  static forget(): void {
    try {
      localStorage.removeItem(LS_KEY);
    } catch {
      /* ignore */
    }
  }
  private remember(): void {
    if (this.corr) return;
    try {
      localStorage.setItem(LS_KEY, JSON.stringify({ code: this.code, token: this.token, pid: this.pid, name: this.name }));
    } catch {
      /* ignore */
    }
  }

  private accept(j: RoomResponse, name: string): void {
    if (!j.code || !j.token || typeof j.pid !== 'number') throw new Error('сервер вернул неполный ответ');
    this.code = j.code;
    this.token = j.token;
    this.pid = j.pid;
    this.name = name;
    this.remember();
    this.connect();
  }

  /** size — игра по переписке на столько мест (сервер требует включённые уведомления) */
  async create(name: string, size = 0): Promise<void> {
    if (size) this.corr = true;
    this.accept(await postJson('api/rooms', { name, client: clientId(), key: deviceKey(), ...(size ? { size } : {}) }), name);
  }

  async join(code: string, name: string): Promise<void> {
    this.accept(await postJson(`api/rooms/${encodeURIComponent(code)}/join`, { name, client: clientId(), key: deviceKey() }), name);
  }

  resume(saved: SavedSession): void {
    this.code = saved.code;
    this.token = saved.token;
    this.pid = saved.pid;
    this.name = saved.name;
    this.connect();
  }

  private connect(): void {
    if (this.closed || this.code == null || this.token == null) return;
    const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
    const base = location.pathname.replace(/[^/]*$/, '');
    const url = `${proto}//${location.host}${base}ws?code=${encodeURIComponent(this.code)}&token=${encodeURIComponent(this.token)}`;
    const ws = new WebSocket(url);
    this.ws = ws;
    ws.onopen = () => {
      this.connected = true;
      this.retry = 0;
      this.handlers.onStatus('connected');
    };
    ws.onmessage = (ev: MessageEvent<string>) => {
      let m: unknown;
      try {
        m = JSON.parse(ev.data);
      } catch {
        return;
      }
      if (!isServerMessage(m)) return;
      if (m.type === 'view') {
        // вошли по коду в партию по переписке — её сессию держит сервер, а не «вернуться в комнату» на хабе
        if (m.room.corr && !this.corr) {
          this.corr = true;
          NetClient.forget();
        }
        this.room = m.room;
        this.pid = m.me;
        this.handlers.onView(m);
      } else this.handlers.onError(m.error);
    };
    ws.onclose = () => {
      this.connected = false;
      this.handlers.onStatus('disconnected');
      if (this.closed) return;
      const delay = Math.min(10000, 1000 * Math.pow(1.6, this.retry++));
      setTimeout(() => this.connect(), delay);
    };
    ws.onerror = () => {
      try {
        ws.close();
      } catch {
        /* ignore */
      }
    };
  }

  private send(obj: unknown): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(obj));
    else this.handlers.onError('нет соединения с сервером');
  }

  act(a: Action): void {
    this.send({ type: 'act', name: a.name, args: a.args });
  }
  start(): void {
    this.send({ type: 'start' });
  }
  /** хост в лобби: партия в режиме ребаланса */
  setRebalance(on: boolean): void {
    this.send({ type: 'rebalance', on });
  }
  /** хост в лобби: комната видна в списке открытых у всех онлайн */
  setOpen(on: boolean): void {
    this.send({ type: 'open', on });
  }
  addBot(level: BotLevel): void {
    this.send({ type: 'addbot', level });
  }

  /** Уйти из комнаты; партия по переписке продолжается без нас — просто закрыть соединение */
  leave(): void {
    this.closed = true;
    if (!this.corr) NetClient.forget();
    try {
      this.ws?.close();
    } catch {
      /* ignore */
    }
  }
}
